import {useMemo} from "react";
import {useTranslation} from "react-i18next";

/**
 * Shared axis builders for the HeatMatrix component. Used by both the analysis page
 * (`ReceivedDataAnalysis`) and the delivery-points export page (`DeliveryPointsExport`) so the
 * two views stay visually consistent.
 *
 * Day and month labels reuse the existing `received_data_analysis_day_*` / `_month_*` translation
 * keys — these are language-only abbreviations (Lun/Mar/…), not page-specific copy, and there is
 * no reason to duplicate them.
 */

export interface AxisEntry {
  value: number;
  label: string;
}

export const useHourRows = (): AxisEntry[] =>
  useMemo(
    () => Array.from({length: 24}, (_, i) => ({value: i, label: `${i}h`})),
    []
  );

export const useDayOfWeekCols = (): AxisEntry[] => {
  const {t} = useTranslation();
  return useMemo(
    () => [
      {value: 1, label: t("received_data_analysis_day_mon")},
      {value: 2, label: t("received_data_analysis_day_tue")},
      {value: 3, label: t("received_data_analysis_day_wed")},
      {value: 4, label: t("received_data_analysis_day_thu")},
      {value: 5, label: t("received_data_analysis_day_fri")},
      {value: 6, label: t("received_data_analysis_day_sat")},
      {value: 7, label: t("received_data_analysis_day_sun")},
    ],
    [t]
  );
};

export const useMonthCols = (): AxisEntry[] => {
  const {t} = useTranslation();
  return useMemo(
    () => [
      {value: 1, label: t("received_data_analysis_month_jan")},
      {value: 2, label: t("received_data_analysis_month_feb")},
      {value: 3, label: t("received_data_analysis_month_mar")},
      {value: 4, label: t("received_data_analysis_month_apr")},
      {value: 5, label: t("received_data_analysis_month_may")},
      {value: 6, label: t("received_data_analysis_month_jun")},
      {value: 7, label: t("received_data_analysis_month_jul")},
      {value: 8, label: t("received_data_analysis_month_aug")},
      {value: 9, label: t("received_data_analysis_month_sep")},
      {value: 10, label: t("received_data_analysis_month_oct")},
      {value: 11, label: t("received_data_analysis_month_nov")},
      {value: 12, label: t("received_data_analysis_month_dec")},
    ],
    [t]
  );
};

/**
 * Year axis builder for the year×month matrix. Fills any gap years between min and max so the
 * axis is contiguous, even when no deliveries land in some years.
 */
export const useYearMonthRows = (
  yearMonthCells: {row: number}[] | undefined
): AxisEntry[] =>
  useMemo(() => {
    if (!yearMonthCells || yearMonthCells.length === 0) return [];
    const years = [...new Set(yearMonthCells.map((c) => c.row))].sort();
    if (years.length < 2) return years.map((y) => ({value: y, label: `${y}`}));
    const minYear = years[0];
    const maxYear = years[years.length - 1];
    const allYears: AxisEntry[] = [];
    for (let y = minYear; y <= maxYear; y++) {
      allYears.push({value: y, label: `${y}`});
    }
    return allYears;
  }, [yearMonthCells]);
